import type { ReactNode } from "react";
import { DropdownMenuItem, cn } from "@wa/ui";

export interface SegOption<T extends string> {
  value: T;
  label: ReactNode;
  title: string;
}

/**
 * A segmented switch inside the account menu: one thumb that slides between
 * equal slots.
 *
 * Ported from the CRM's account menu (`web_crm/src/crm/shell/Seg.tsx`), which
 * writes the same control in the CRM's own stylesheet vocabulary; this hub is
 * plain Tailwind, so the rules are inline here rather than shared.
 *
 * Every slot is a kit `DropdownMenuItem` with the `menuitemradio` role, so the
 * menu's arrow keys reach it and a screen reader hears which one is on. A menu
 * only moves focus between its own items and swallows Tab, so plain buttons
 * here could not be reached without a mouse. For use inside a
 * `DropdownMenuContent` only.
 *
 * Picking a slot keeps the menu open (`preventDefault` in `onSelect`): the whole
 * point of a switch is that you see what it did without reopening anything to
 * check.
 *
 * The thumb is one element sliding, not a background moving between slots:
 * that is what makes the movement readable as "the same control changed", and
 * it survives any number of slots because both the width and the travel are
 * derived from the option count.
 */
export function Seg<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
  label: string;
}) {
  const index = Math.max(0, options.findIndex((o) => o.value === value));
  return (
    <div
      role="group"
      aria-label={label}
      className="relative flex shrink-0 rounded-full bg-muted/70 p-[3px]"
    >
      <span
        aria-hidden
        className="absolute inset-y-[3px] left-[3px] rounded-full bg-card shadow-sm transition-transform duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none"
        style={{
          width: `calc((100% - 6px) / ${options.length})`,
          transform: `translateX(${index * 100}%)`,
        }}
      />
      {options.map((o) => {
        const on = o.value === value;
        return (
          <DropdownMenuItem
            key={o.value}
            role="menuitemradio"
            aria-checked={on}
            aria-label={o.title}
            title={o.title}
            // The slots are icons, so name them for the menu's type-to-find.
            textValue={o.title}
            onSelect={(e) => {
              e.preventDefault();
              onChange(o.value);
            }}
            className={cn(
              // The thumb shows the choice, so focus only lifts the text and the
              // keyboard gets an inset ring.
              "z-[1] h-7 flex-1 cursor-pointer justify-center gap-1.5 rounded-full px-3 py-0 text-[11.5px] font-bold",
              "focus:bg-transparent focus:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/55",
              on ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {o.label}
          </DropdownMenuItem>
        );
      })}
    </div>
  );
}
